import { MeasureDomainService } from './domain';
import { MeasureFactory } from './entity/measureFactory';

import type { MeasureIRepository } from './entity/measureIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { MeasureJson, MeasureJsonPvt } from '@/types/domain/measure/entities/measureEntity';

/**
 * 測定アプリケーションサービス
 */
export class MeasureApplicationService {
  private service!: MeasureDomainService;

  private measureFactory = new MeasureFactory();

  /**
   * Injection
   * @param measureRepository measureRepository
   * @param logger logger
   */
  public inject(
    measureRepository: MeasureIRepository,
    logger: Logger,
  ) {
    this.service = new MeasureDomainService(logger);
    this.service.inject(measureRepository);
  }

  /**
   * getMeasureList
   * @param token トークン
   * @returns 結果
   */
  async getMeasureList(token: string | null) {
    const result = await this.service.getMeasureList(token);
    return result;
  }

  /**
   * createMeasure
   * @param input 入力
   * @returns 結果
   */
  async createMeasure(
    input: MeasureJson,
  ) {
    const entity = this.measureFactory.createFromJson(input);
    const result = await this.service.createMeasure(entity);
    return result;
  }

  /**
   * updateMeasure
   * @param input 入力
   * @returns 結果
   */
  async updateMeasure(
    input: MeasureJson,
  ) {
    const entity = this.measureFactory.createFromJson(input);
    const result = await this.service.updateMeasure(entity);
    return result;
  }

  /**
   * deleteMeasure
   * @param input 入力
   * @returns 結果
   */
  async deleteMeasure(
    input: MeasureJson,
  ) {
    const entity = this.measureFactory.createFromJson(input);
    const result = await this.service.deleteMeasure(entity);
    return result;
  }

  /**
   * 測定リストを更新する(Create)
   * @param measureList 測定リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createMeasureStore(
    measureList: MeasureJson[],
    payload: EntitySubscribeType,
    filter: ((json: MeasureJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      measureList,
      payload,
      this.measureFactory,
      filter,
    );
  }

  /**
   * 測定リストを更新する(Update)
   * @param measureList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateMeasureStore(
    measureList: MeasureJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      measureList,
      payload,
      this.measureFactory,
    );
  }

  /**
   * 測定リストを更新する(Delete)
   * @param measureList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteMeasureStore(
    measureList: MeasureJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      measureList,
      payload,
      this.measureFactory,
    );
  }
}
